import { SHA256 } from "crypto-js";
import toCDNUrl from "./toCDNUrl";

type ImageData = {
  id: string;
  data: string;
};

type CachedDataConfig = {
  resizeImageSize?: number;
};

export class CachedDataUrl {
  private imageMap: Map<string, ImageData | null> = new Map();
  private imageSize: number = 720;

  constructor(config?: CachedDataConfig) {
    if (config?.resizeImageSize !== undefined) {
      this.imageSize = config.resizeImageSize;
    }
  }

  public async getDataUrls(iconUrls: string[]) {
    const tasks = iconUrls.map(
      async (iconUrl): Promise<[string, ImageData | null]> => {
        const imageData = this.imageMap.get(iconUrl);
        if (imageData) {
          return [iconUrl, imageData];
        }

        const newData = await this.createImageDataByImageUrl(iconUrl);
        this.imageMap.set(iconUrl, newData);
        return [iconUrl, newData];
      }
    );
    const imageDatas = await Promise.all(tasks);
    return new Map(imageDatas);
  }

  private async createImageDataByImageUrl(url: string) {
    if (!url) return null;
    const cdnUrl = toCDNUrl(url);
    let imageBitmap: ImageBitmap | null = null;

    try {
      // URLをfetchしてImageBitmapを作成
      const res = await fetch(cdnUrl);
      if (!res.ok) return null;
      const blob = await res.blob();
      imageBitmap = await createImageBitmap(blob);

      // 画像をリサイズ
      const canvas = this.resizeImageAsCanvas(imageBitmap);
      if (!canvas) return null;

      // daraURLを作成
      const dataUrl = canvas.toDataURL(blob.type);
      const id = SHA256(dataUrl).toString();

      return {
        id,
        data: dataUrl,
      };
    } catch {
      return null;
    } finally {
      imageBitmap?.close();
    }
  }

  private resizeImageAsCanvas(bmp: ImageBitmap) {
    let width = bmp.width;
    let height = bmp.height;
    if (width > this.imageSize || height > this.imageSize) {
      const scale = Math.min(this.imageSize / width, this.imageSize / height);

      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    return canvas;
  }
}
